"use client";

import { memo } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";

type LogoProps = {
  variant?: "default" | "compact";
  className?: string;
  href?: string;
};

// The tight assets, not the "optimized" ones. logo-full-optimized is an 800x800 canvas
// with the mark occupying only 625x181 of it, so sizing it by height rendered the brand
// mark at under a quarter of what the layout asked for, floating in empty space. The
// icon files had the same problem at about a third. The tight files are the same artwork
// cropped to its own bounds, regenerated from the 5000px brand master, and they are
// smaller on disk as well.
//
// Memoized Logo component to prevent re-renders on route changes
// CRITICAL: Use plain <img> instead of Next.js Image for maximum compatibility with
// iOS WebView/Capacitor. Next.js Image can fail to render in native WebViews, causing
// the in-app logo to disappear (cross-device regression). Same approach as LoadingScreen.
// Sizes are for a TIGHT asset. They used to be roughly double, because the logo file was
// mostly empty canvas and only about half its height was the actual mark. Shrinking the
// boxes alongside the asset swap keeps the logo the size it looks on the page.
export const Logo = memo(function Logo({ variant = "default", className, href = "/dashboard" }: LogoProps) {
  const content = (
    <div className={variant === "compact" ? "flex items-center justify-center" : cn("flex items-center", className)}>
      {variant === "default" ? (
        // Full logo with text (for headers)
        // Use <picture> with PNG fallback for iOS WebView compatibility
        <div className={cn("relative h-10 w-auto md:h-12", className)}>
          <picture>
            <source srcSet="/logo-full-tight.webp" type="image/webp" />
            <img
              src="/logo-full-tight.png"
              alt="GrowthSync"
              width={200}
              height={112}
              className="h-10 w-auto object-contain md:h-12"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        </div>
      ) : (
        // Icon only (for compact/icon-only contexts)
        // Use <picture> with PNG fallback for iOS WebView - WebP can fail to load in some Capacitor/WKWebView setups
        <div className={cn("relative shrink-0 flex items-center justify-center overflow-hidden", className || "h-10 w-10 md:h-12 md:w-12")}>
          <picture>
            <source srcSet="/logo-icon-tight.webp" type="image/webp" />
            <img
              src="/logo-icon-tight.png"
              alt="GrowthSync"
              width={112}
              height={112}
              className="h-full w-full object-contain object-center"
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        </div>
      )}
    </div>
  );

  if (href) {
    return <Link href={href}>{content}</Link>;
  }

  return content;
});

